/**
 * AI-generated code, moderated by lzygmanski
 */
import dayjs from 'dayjs';
import {LineChartIcon} from 'lucide-react';
import {useCallback, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import InfoState from '@/components/InfoState';
import {Button} from '@/components/ui/button';
import type {DialogProps} from '@/components/ui/dialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogSection,
  DialogTitle,
} from '@/components/ui/dialog';
import {Input, InputItem, InputLabel} from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {useToast} from '@/components/ui/use-toast';
import {getErrorMessage} from '@/errors/getErrorMessage';
import {useAuthorize} from '@/hooks/useAuth';
import {graphqlClient} from '@/services/graphqlClient';
import {
  useAddHealthLogMeasurementsMutation,
  useGetAllCurrentResidentsForInputQuery,
  useGetRoomsQuery,
  useListHealthLogMetricsQuery,
} from '@/services/graphqlHooks';
import queryClient from '@/services/queryClient';
import healthLogMetrics from '@/translation/healthLogMetrics';
import {throwErrorMessage} from '@/utils/throwErrorMessage';
import {TIME_FORMATS} from '@/utils/timeFormats';

import type {MeasurementValuesMap} from '../types/healthLogMeasurementsDialog';
import {
  buildMeasurementInputs,
  mergeResidentsWithRooms,
} from '../utils/healthLogMeasurementsDialogUtils';
import HealthLogMeasurementsTable from './HealthLogMeasurementsTable';

export type AddHealthLogMeasurementsDialogProps = DialogProps;

function AddHealthLogMeasurementsDialog({
  onOpenChange,
  ...props
}: AddHealthLogMeasurementsDialogProps) {
  const {t} = useTranslation();
  const {toast} = useToast();

  const canAddHealthLog = useAuthorize('CREATE_HEALTH_LOG_MEASUREMENT');

  const [selectedMetricId, setSelectedMetricId] = useState<string>('');
  const [recordedAt, setRecordedAt] = useState<string>(() =>
    dayjs().format(TIME_FORMATS.dateTimeHTMLLocal)
  );
  const [measurementValues, setMeasurementValues] =
    useState<MeasurementValuesMap>(new Map());

  // Fetch active residents
  const {data: residentsData, isLoading: isLoadingResidents} =
    useGetAllCurrentResidentsForInputQuery(graphqlClient);

  // Fetch rooms
  const {data: roomsData} = useGetRoomsQuery(graphqlClient);

  // Fetch metric definitions
  const {data: metricsData, isLoading: isLoadingMetrics} =
    useListHealthLogMetricsQuery(graphqlClient);

  // Merge residents with rooms (no nested loops, memoized)
  const residentsWithRooms = useMemo(() => {
    const residents = residentsData?.getAllCurrentResidents ?? [];
    const rooms = roomsData?.getRooms ?? [];
    return mergeResidentsWithRooms(residents, rooms);
  }, [residentsData, roomsData]);

  // Filter enabled metrics
  const metrics = useMemo(
    () =>
      (metricsData?.listHealthLogMetrics ?? []).filter(
        metric => metric.enabled
      ),
    [metricsData]
  );

  const selectedMetric = useMemo(
    () => metrics.find(metric => metric.id === selectedMetricId),
    [metrics, selectedMetricId]
  );

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries({queryKey: ['GetHealthLogMeasurements']});
  }, []);

  const addMeasurements = useAddHealthLogMeasurementsMutation(graphqlClient, {
    onSuccess,
  });

  // Handle input change for a resident's measurement field
  const handleValueChange = useCallback(
    (residentId: string, fieldName: string, value: string) => {
      setMeasurementValues(prev => {
        const newMap = new Map(prev);
        const existingValues = newMap.get(residentId) ?? {};
        newMap.set(residentId, {
          ...existingValues,
          [fieldName]: value,
        });
        return newMap;
      });
    },
    []
  );

  const handleSubmit = async () => {
    try {
      if (!selectedMetric) return;

      if (dayjs(recordedAt).isAfter(dayjs())) {
        toast({
          title: t('Recorded time cannot be in the future'),
          variant: 'destructive',
        });
        return;
      }

      const result = buildMeasurementInputs(
        measurementValues,
        selectedMetric,
        recordedAt
      );

      if (!result || result.error) {
        toast({
          title: t('Validation error'),
          description: result?.error ?? getErrorMessage(result?.error),
          variant: 'destructive',
        });
        return;
      }

      await addMeasurements.mutateAsync({
        input: {
          measurements: result.inputs,
        },
      });

      toast({
        title: t('Measurements added'),
        description: t('{{count}} measurement(s) added successfully', {
          count: result.inputs.length,
        }),
        variant: 'success',
      });

      handleClose();
    } catch (error) {
      throwErrorMessage(error);
    }
  };

  const handleClose = () => {
    // Reset state
    setSelectedMetricId('');
    setRecordedAt(dayjs().format(TIME_FORMATS.dateTimeHTMLLocal));
    setMeasurementValues(new Map());
    onOpenChange?.(false);
  };

  const showTable = selectedMetricId && selectedMetric;
  const isSubmitDisabled =
    !canAddHealthLog ||
    addMeasurements.isPending ||
    !selectedMetricId ||
    measurementValues.size === 0;

  return (
    <Dialog {...props} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white md:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{t('Add group healthlog measurements')}</DialogTitle>
        </DialogHeader>
        <DialogSection className="flex flex-col gap-7">
          {/* Step 1: Select measurement type and date */}
          <div className="flex flex-col gap-4 md:flex-row md:gap-6">
            <InputItem className="flex-1">
              <InputLabel htmlFor="metricId">{t('Metric')}</InputLabel>
              <Select
                value={selectedMetricId}
                onValueChange={setSelectedMetricId}
                disabled={isLoadingMetrics}
              >
                <SelectTrigger id="metricId">
                  <SelectValue placeholder={t('Select measurement type')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    {metrics.map(metric => (
                      <SelectItem key={metric.id} value={metric.id}>
                        {healthLogMetrics[metric.id] || metric.id}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </InputItem>
            <InputItem className="flex-1">
              <InputLabel htmlFor="recordedAt">{t('Date and time')}</InputLabel>
              <Input
                id="recordedAt"
                type="datetime-local"
                value={recordedAt}
                onChange={e => setRecordedAt(e.target.value)}
              />
            </InputItem>
          </div>

          {/* Step 2: Show table if metric selected */}
          {showTable && (
            <div className="flex flex-col gap-7">
              {isLoadingResidents ? (
                <div className="flex h-64 items-center justify-center">
                  <InfoState
                    icon={<LineChartIcon size={40} className="text-mint-500" />}
                    title={t('Loading residents...')}
                  />
                </div>
              ) : residentsWithRooms.length === 0 ? (
                <div className="flex h-64 items-center justify-center">
                  <InfoState
                    icon={<LineChartIcon size={40} className="text-mint-500" />}
                    title={t('No active residents')}
                    description={t(
                      'There are no active residents to add measurements for'
                    )}
                  />
                </div>
              ) : (
                <HealthLogMeasurementsTable
                  residents={residentsWithRooms}
                  measurementValues={measurementValues}
                  selectedMetric={selectedMetric}
                  onValueChange={handleValueChange}
                />
              )}
            </div>
          )}

          {!showTable && (
            <div className="flex h-64 items-center justify-center">
              <InfoState
                icon={<LineChartIcon size={40} className="text-mint-500" />}
                title={t('Select a measurement type')}
                description={t(
                  'Choose a measurement type to view and edit measurements for all active residents'
                )}
              />
            </div>
          )}
        </DialogSection>
        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange?.(false)}
          >
            {t('Cancel')}
          </Button>
          <Button
            disabled={isSubmitDisabled}
            onClick={handleSubmit}
            variant="primary"
          >
            {addMeasurements.isPending ? t('Submitting...') : t('Submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddHealthLogMeasurementsDialog;
